/**
 * A WHATSAPP LINK THAT OPENS THE APP FROM INSIDE AN IN-APP BROWSER, AND SAYS SO WHEN IT CANNOT.
 *
 * It is the plain anchor it always was, and since 2026-10-14 it carries the plain `https://wa.me/…` link
 * EVERYWHERE — inside Facebook's, Instagram's and Messenger's browser too. wa.me is an iOS universal link and an
 * Android app link, and a phone honours one only for a user-initiated navigation in the same frame: so in-app
 * there is no `target`, no `window.open`, no scripted navigation and no `preventDefault` — the tap IS the
 * navigation, exactly as it is for the Google reviews link on the same page, which opens the Google app from
 * inside Instagram. The rewrites that came before (`whatsapp://`, then Safari's door and WhatsApp's intent) are
 * written up in utils/whatsappLink.js with the logs that killed each one.
 *
 * Outside an in-app browser the link still opens in a new tab, as it always did.
 *
 * What stays either way is the watch: if the page is still in front a moment after an in-app tap — nothing
 * opened — a small sheet offers the app by name, the phone's own browser, and the number to copy. Leaving the
 * page (the app opened, or the system prompt took over) cancels the sheet before it is ever shown.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { inAppBrowserNow, osNow, parseWaMe, whatsappAppHref, whatsappHrefFor } from '../../utils/whatsappLink';
import { browserNameFor, openInBrowserHref } from '../../utils/openInBrowser';
import styles from './whatsAppLink.module.css';

/** How long after the tap the page may still be in front before the sheet offers a way out. */
export const FALLBACK_AFTER_MS = 1500;

export default function WhatsAppLink({ href: waMeUrl, className, children, testId, ariaLabel, onClick, ...rest }) {
  const { t } = useTranslation();
  const inApp = inAppBrowserNow();
  const os = osNow();
  // The wa.me link itself, in every browser (utils/whatsappLink.js) — named, so there is one place that says so.
  const href = whatsappHrefFor(waMeUrl);
  const parsed = parseWaMe(waMeUrl);
  const [stuck, setStuck] = useState(false);
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  const left = useRef(null);

  const stopWatching = useCallback(() => {
    clearTimeout(timer.current);
    if (left.current) {
      document.removeEventListener('visibilitychange', left.current);
      window.removeEventListener('pagehide', left.current);
      window.removeEventListener('blur', left.current);
      left.current = null;
    }
  }, []);
  useEffect(() => stopWatching, [stopWatching]);

  const watchTheTap = useCallback(() => {
    stopWatching();
    // Leaving the page — the app opened, or the system asked — means there is nothing to offer.
    left.current = () => stopWatching();
    document.addEventListener('visibilitychange', left.current);
    window.addEventListener('pagehide', left.current);
    window.addEventListener('blur', left.current);
    timer.current = setTimeout(() => {
      stopWatching();
      if (document.visibilityState === 'visible') setStuck(true);
    }, FALLBACK_AFTER_MS);
  }, [stopWatching]);

  const handleClick = (e) => {
    if (onClick) onClick(e);
    if (inApp && parsed) watchTheTap();
  };

  const copyNumber = async () => {
    try {
      await navigator.clipboard.writeText(`+${parsed.phone}`);
      setCopied(true);
    } catch (_) {
      setCopied(false);
    }
  };

  return (
    <>
      <a
        href={href}
        onClick={handleClick}
        className={className}
        data-testid={testId}
        aria-label={ariaLabel}
        {...(inApp ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
        {...rest}
      >
        {children}
      </a>
      {/* Not a dialog: the page stays usable underneath and nothing is trapped — a notice with a way out. */}
      {stuck && parsed && (
        <div className={styles.sheet} role="region" aria-label={t('whatsapp.didNotOpen', { defaultValue: "WhatsApp didn't open?" })} aria-live="polite" data-testid="whatsapp-fallback">
          <button type="button" className={styles.close} aria-label={t('common.close', { defaultValue: 'Close' })} onClick={() => setStuck(false)}>×</button>
          <p className={styles.title}>{t('whatsapp.didNotOpen', { defaultValue: "WhatsApp didn't open?" })}</p>
          <p className={styles.number}>+{parsed.phone}</p>
          <div className={styles.actions}>
            <a href={whatsappAppHref(waMeUrl)} className={styles.primary} data-testid="whatsapp-fallback-open" onClick={watchTheTap}>
              {t('whatsapp.openApp', { defaultValue: 'Open WhatsApp' })}
            </a>
            <a href={openInBrowserHref(waMeUrl, os)} className={styles.secondary} data-testid="whatsapp-fallback-browser" onClick={watchTheTap}>
              {t('whatsapp.openIn', { browser: browserNameFor(os), defaultValue: 'Open in {{browser}}' })}
            </a>
          </div>
          <button type="button" className={styles.secondary} data-testid="whatsapp-fallback-copy" onClick={copyNumber}>
            {copied ? t('whatsapp.copied', { defaultValue: 'Copied' }) : t('whatsapp.copyNumber', { defaultValue: 'Copy the number' })}
          </button>
        </div>
      )}
    </>
  );
}
